import { useEffect, useRef, useState } from 'react';
import distributorService from '../services/distributorService';
import { stateCode } from '../data/usStates';
import { isSessionCurrent } from '../utils/authSession';

/** The server owns the price; draft edits invalidate its visible acceptance. */
export default function useDistributorCashQuote({
  lines,
  address,
  po,
  carrier,
  speed,
  quote,
  pricing,
  options,
  session,
  clinicId,
  clinicMode,
  pricingAvailable,
  inFlight,
  pendingPayload,
  mounted,
  setError,
  setAccepted,
}) {
  const [cashQuote, setCashQuote] = useState(null);
  const [quotingCash, setQuotingCash] = useState(false);
  const cashRequest = useRef(0);
  const quoteKey = useRef('');
  const invalidateCashQuote = () => {
    cashRequest.current += 1;
    quoteKey.current = window.crypto?.randomUUID?.() || '';
    setCashQuote(null);
    setAccepted(false);
  };
  useEffect(() => {
    cashRequest.current += 1;
    quoteKey.current = window.crypto?.randomUUID?.() || '';
    setCashQuote(null);
    setAccepted(false);
  }, [lines, address, po, carrier, speed, quote?.deliveryDate, pricing?.revision, setAccepted]);
  const reviewCashQuote = async () => {
    if (inFlight.current || quotingCash || pendingPayload.current || !isSessionCurrent(session))
      return;
    const selectedLines = lines.map((line) => ({
      product: line.product,
      size: options?.products?.find((p) => p._id === line.product)?.size,
      quantity: Number(line.quantity),
    }));
    const destination = Object.fromEntries(
      Object.entries(address).map(([key, value]) => [key, value.trim()])
    );
    if (
      !quoteKey.current ||
      !quote ||
      !pricingAvailable ||
      !selectedLines.length ||
      selectedLines.some(
        (line) =>
          !line.size ||
          !Number.isSafeInteger(line.quantity) ||
          line.quantity < 1 ||
          line.quantity > 1000
      ) ||
      Object.values(destination).some((value) => !value) ||
      !stateCode(destination.state)
    ) {
      setError(
        'Choose products, quantities and a complete delivery address before reviewing the total.'
      );
      return;
    }
    const generation = ++cashRequest.current;
    setQuotingCash(true);
    setError('');
    try {
      const data = (
        await distributorService.cashOrderQuote({
          idempotencyKey: quoteKey.current,
          clinic: clinicId,
          expectedPricingRevision: pricing.revision,
          ...(clinicMode ? { capability: 'clinic-orders-v1' } : {}),
          lineItems: selectedLines,
          shippingScheduleVersion: 1,
          shippingSpeed: speed,
          deliveryDate: quote.deliveryDate,
          preferredCarrier: carrier,
          address: destination,
          ...(po.trim() ? { patientName: po.trim() } : {}),
        })
      )?.data?.data;
      if (
        /^[a-f\d]{24}$/i.test(data?.cashQuoteId) &&
        data.currency === 'USD' &&
        typeof data.expiresAt === 'string' &&
        Number.isFinite(Date.parse(data.expiresAt)) &&
        Date.parse(data.expiresAt) <= Date.now()
      ) {
        if (mounted.current && generation === cashRequest.current && isSessionCurrent(session)) {
          invalidateCashQuote();
          setError('This delivery quote expired. Review the current delivery charge again.');
        }
        return;
      }
      if (
        !/^[a-f\d]{24}$/i.test(data?.cashQuoteId) ||
        data.currency !== 'USD' ||
        !Number.isSafeInteger(data.shippingCents) ||
        data.shippingCents < 0 ||
        !Number.isFinite(Date.parse(data.expiresAt)) ||
        Date.parse(data.expiresAt) <= Date.now() ||
        (!clinicMode &&
          (!Number.isSafeInteger(data.productSubtotalCents) ||
            data.productSubtotalCents <= 0 ||
            !Number.isSafeInteger(data.totalCents) ||
            data.totalCents !== data.productSubtotalCents + data.shippingCents))
      )
        throw new Error('Cash quote could not be verified');
      if (mounted.current && generation === cashRequest.current && isSessionCurrent(session)) {
        setCashQuote(data);
        setAccepted(false);
      }
    } catch {
      if (mounted.current && generation === cashRequest.current && isSessionCurrent(session))
        setError('The delivery charge could not be confirmed. Review the order and try again.');
    } finally {
      if (mounted.current && isSessionCurrent(session)) setQuotingCash(false);
    }
  };
  const validateCashAcceptance = (accepted) => {
    if (pendingPayload.current) return true;
    if (!cashQuote || !accepted || Date.parse(cashQuote.expiresAt) <= Date.now()) {
      if (cashQuote && Date.parse(cashQuote.expiresAt) <= Date.now()) invalidateCashQuote();
      setError('Review and accept a current delivery charge before submitting.');
      return false;
    }
    return true;
  };
  return { cashQuote, invalidateCashQuote, quotingCash, reviewCashQuote, validateCashAcceptance };
}
